import { Type, Static } from '@sinclair/typebox';
import {
  defineSchema,
  SystemFields,
  StringField,
  TextField,
  IntegerField,
  BooleanField,
  SlugField,
  RelationOne,
  RelationField,
} from '@ayocore/platform/common/helpers/schema-types';
import leadTypeSchemaDefinition from './lead-type.schema';

import { name as moduleName } from '../module.json';
/**
 * Lead Status Schema - User-configurable lead statuses
 *
 * Allows users to define their own lead pipeline stages.
 * Default statuses: New → Contacted → Qualified → Proposal → Won/Lost
 */

// Complete schema (with system fields)
export const LeadStatusSchema = Type.Object({
  ...SystemFields, // Platform-provided system fields (id, created_at, updated_at)

  name: StringField({
    title: 'Название статуса',
    minLength: 1,
    maxLength: 100,
    description: 'Название статуса (например, "Новый", "Связались", "Выиграли")',
  }),

  slug: SlugField({
    title: 'Slug',
    description: 'Unique constant identifier (NEW, CONTACTED, WON, LOST, etc.)',
  }),

  color: StringField({
    title: 'Цвет',
    maxLength: 7,
    default: '#3b82f6',
    description: 'Hex-код цвета для отображения в интерфейсе',
  }),

  sort_order: IntegerField({
    title: 'Порядок сортировки',
    description: 'Порядок отображения в воронке (0 = первый)',
  }),

  /**
   * Плановое время нахождения лида в этом статусе (в днях).
   *
   * Используется для подсветки просроченных лидов в списках и карточке.
   *  - null или 0 = нет явного плана (не подсвечиваем по этому статусу)
   */
  planned_duration_days: IntegerField({
    title: 'План, дней',
    nullable: true,
    description: 'Ожидаемое количество дней нахождения лида в этом статусе',
  }),

  is_active: BooleanField({
    title: 'Активный',
    default: true,
    description: 'Активен ли этот статус и доступен для выбора',
  }),

  is_final: BooleanField({
    title: 'Финальный статус',
    default: false,
    description: 'Является ли это финальным статусом (Выиграли/Проиграли)',
  }),

  is_negative: BooleanField({
    title: 'Негативный исход',
    default: false,
    description: 'Негативный финальный статус (потеря, проигрыш)',
  }),

  is_positive: BooleanField({
    title: 'Позитивный исход',
    default: false,
    description: 'Позитивный финальный статус (успешное закрытие)',
  }),

  require_comment: BooleanField({
    title: 'Требовать комментарий',
    nullable: true,
    default: null,
    description: 'При переходе в этот статус менеджер должен заполнить комментарий',
  }),

  manager_text: TextField({
    title: 'Текст для менеджера',
    nullable: true,
    maxLength: 3000,
    description: 'Подсказка в окне смены статуса. Пустое значение скрывает подсказку',
  }),

  require_deal: BooleanField({
    title: 'Требовать сделку',
    default: false,
    description: 'При переходе в этот статус к лиду должна быть привязана сделка',
  }),

  description: StringField({
    title: 'Описание',
    nullable: true,
    maxLength: 500,
    description: 'Опциональное описание того, что означает этот статус',
  }),

  lead_type: RelationOne(leadTypeSchemaDefinition), // For TypeScript type
  lead_type_id: RelationField(leadTypeSchemaDefinition, 'ID типа лида', {
    description: 'ID типа лида, к которому относится этот статус',
    relationName: 'lead_type',
  }),
});

// TypeScript types derived from schemas
export type LeadStatus = Static<typeof LeadStatusSchema>;

// Schema definition with automatic table prefixing
export default defineSchema({
  moduleName,
  tableName: 'lead_statuses', // System will prefix with 'crm_'
  schema: LeadStatusSchema,
  additionalIndexes: [
    'name',
    'sort_order',
    'is_active',
    { columns: ['lead_type_id', 'slug'], unique: true },
  ],
});
